/**
 * @file BadgeCarousel.jsx
 * @description A glitzy, auto-scrolling carousel that showcases the user's earned achievements.
 *              Displays custom BadgeImage components with 3D-effect navigation and progress indicators.
 *              Includes fallback states for loading and empty achievement sets.
 * 
 * @module components/BadgeCarousel
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  FiTarget,
  FiChevronLeft,
  FiChevronRight,
  FiExternalLink,
  FiAward
} from 'react-icons/fi';
import { FaFire, FaTrophy } from 'react-icons/fa';
import BadgeImage from './BadgeImage';
import { apiGet } from '../utils/apiUtils';

/* ══════════════════════════════════════════════════════════════
 * HELPERS
 * ══════════════════════════════════════════════════════════════ */

/**
 * Maps internal badge names to React-Icon components.
 */
const getBadgeIcon = (badgeName) => {
  switch (badgeName) {
    case 'streak_master': return FaFire;
    case 'problem_crusher': return FiTarget;
    case 'daily_champion': return FaTrophy;
    default: return FiAward;
  }
};

/**
 * Returns color tokens for different achievement tiers (Bronze, Silver, Gold, Platinum).
 */
const getTierColor = (tier) => {
  switch (tier?.toLowerCase()) {
    case 'bronze': return { bg: 'from-amber-600 to-amber-700', border: 'border-amber-500', text: 'text-amber-400' };
    case 'silver': return { bg: 'from-slate-400 to-slate-500', border: 'border-slate-400', text: 'text-slate-300' };
    case 'gold': return { bg: 'from-yellow-500 to-yellow-600', border: 'border-yellow-400', text: 'text-yellow-300' };
    case 'platinum': return { bg: 'from-purple-500 to-purple-600', border: 'border-purple-400', text: 'text-purple-300' };
    default: return { bg: 'from-gray-600 to-gray-700', border: 'border-gray-500', text: 'text-gray-400' };
  }
};

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component BadgeCarousel
 * @description Renders a cycle-able carousel of user badges.
 *              Auto-advances every 4 seconds unless manually interrupted.
 * 
 * @param {Object} props
 * @param {string} props.userId - The ID of the authenticated user.
 * @param {string} props.token - JWT token for authenticated badge fetching.
 * @param {Function} props.onViewAll - Callback function (usually navigation) for the 'View All' action.
 */
const BadgeCarousel = ({ userId, token, onViewAll }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  /* ── Data Fetching ── */

  /**
   * Fetches the full list of badges earned by the user.
   */
  useEffect(() => {
    const fetchBadges = async (currentUserId) => {
      try {
        setLoading(true);
        const response = await apiGet(`/badges/user/${currentUserId}`);

        if (response.ok) {
          const data = await response.json();
          setBadges(data.badges || []);
        } else {
          console.error('Badge fetch failed:', response.status);
        }
      } catch (error) {
        console.error('Network error fetching badges:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchBadges(userId);
    }
  }, [userId]);

  /* ── Navigation Logic ── */

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : badges.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < badges.length - 1 ? prev + 1 : 0));
  };

  /**
   * Effect: Periodic interval for auto-scrolling through the carousel.
   */
  useEffect(() => {
    if (badges.length <= 1) return;
    const interval = setInterval(goToNext, 4000);
    return () => clearInterval(interval);
  }, [badges.length]);

  /* ── Conditional States ── */

  if (loading) {
    return (
      <div>
        <div className="flex space-x-3">
          {[1, 2, 3].map(i => <div key={i} className="flex-1 h-20 bg-gray-100 rounded-lg animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (badges.length === 0) {
    return (
      <div className="text-center py-6">
        <FiAward className="text-3xl text-muted-foreground mx-auto mb-2" />
        <p className="text-xs text-muted-foreground">No badges earned yet</p>
      </div>
    );
  }

  const showNavigation = badges.length > 1;

  /* ── Render ── */
  return (
    <div className="w-full">
      {/* Flex container: Left Arrow | Badge Viewport | Right Arrow */}
      <div className="flex items-center justify-between gap-2">
        {/* Left Navigation Arrow */}
        {showNavigation ? (
          <button
            onClick={goToPrevious}
            className="flex-shrink-0 z-10 w-7 h-7 bg-emerald-600 hover:bg-emerald-700 rounded-full flex items-center justify-center text-white shadow-md transition-all active:scale-90 hover:shadow-lg"
            aria-label="Previous Badge"
          >
            <FiChevronLeft className="w-3.5 h-3.5" />
          </button>
        ) : (
          <div className="w-7 flex-shrink-0" />
        )}

        {/* Carousel Window (Sliding Viewport) */}
        <div className="overflow-hidden flex-1">
          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {badges.map((badge, index) => {
              return (
                <div key={index} className="w-full flex-shrink-0 flex justify-center items-center">
                  <BadgeImage
                    badgeId={badge.badge_types?.id}
                    imagePath={badge.badge_types?.image_path}
                    badgeTitle={badge.badge_types?.badge_title}
                    badgeType={badge.badge_types?.badge_name}
                    tier={badge.badge_types?.badge_tier}
                    isEarned={true}
                    size="xl"
                    className=""
                    earnedDescription={badge.badge_types?.earned_description}
                    lockedDescription={badge.badge_types?.locked_description}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Navigation Arrow */}
        {showNavigation ? (
          <button
            onClick={goToNext}
            className="flex-shrink-0 z-10 w-7 h-7 bg-emerald-600 hover:bg-emerald-700 rounded-full flex items-center justify-center text-white shadow-md transition-all active:scale-90 hover:shadow-lg"
            aria-label="Next Badge"
          >
            <FiChevronRight className="w-3.5 h-3.5" />
          </button>
        ) : (
          <div className="w-7 flex-shrink-0" />
        )}
      </div>

      {/* Dynamic Progress Dots */}
      {showNavigation && (
        <div className="flex justify-center mt-3 space-x-1.5">
          {badges.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${currentIndex === index ? 'bg-emerald-600 w-4' : 'bg-gray-300 hover:bg-gray-400 w-1.5'
                }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BadgeCarousel;